'use client';

import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AddressFields } from '@sola/ark-core/molecules/address-fields/address-fields.tsx';
import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import { useAddressUpdate } from '@sola/my-sdk/settings/hooks.ts';
import { CheckoutAddressTypeEnum } from '@sola/schema/dotcom/checkout/contactInfo.ts';
import type { MailingAddress as MailingAddressModal } from '@sola/schema/dotcom/shared/address.ts';
import { MailingAddressEnum } from '@sola/schema/dotcom/shared/address.ts';
import {
  COUNTRY_CODE_US,
  mapCountryNameToCode,
} from '@sola/schema/dotcom/shared/geo.ts';
import type { GraphQLSupporterAddress } from '@sola/schema/dotcom/supporters/schema.generated.js';

import {
  LocalAlertsProvider,
  useLocalAlerts,
} from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';
import {
  createAddressUpdateErrorAlert,
  createAddressUpdateSuccessAlert,
} from '../../utils/alerts.tsx';
import { mapAddressDetails } from '../../utils/map-address-Details.ts';

const MailingModalForm = ({
  defaultFormValues,
  onSuccess,
}: {
  defaultFormValues: MailingAddressModal;
  onSuccess: () => void;
}) => {
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const { mutateAsync: updateMailingAddress } = useAddressUpdate();
  const [isBusy, setIsBusy] = useState(false);
  const { t } = useTranslation(['core', 'my-settings']);

  const handleSubmit = async (data: FieldValues) => {
    setIsBusy(true);
    clearAlerts();
    try {
      await updateMailingAddress({
        address: mapAddressDetails(data as MailingAddressModal),
      });
      onSuccess();
    } catch {
      addAlerts(createAddressUpdateErrorAlert());
      setIsBusy(false);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      options={{
        defaultValues: defaultFormValues,
        mode: 'onChange',
      }}
    >
      <AddressFields
        addressType={CheckoutAddressTypeEnum.Mailing}
        defaultCountry={COUNTRY_CODE_US}
      />
      <DialogActions>
        <DialogActionClose>{t('core:actions.cancel')}</DialogActionClose>
        <DialogActionPrimary type="submit" isBusy={isBusy} disableUntilFilled>
          {t('core:actions.save')}
        </DialogActionPrimary>
      </DialogActions>
    </Form>
  );
};

export const SettingsModalMailing = ({
  addressDetails,
  openTrigger,
}: {
  addressDetails: GraphQLSupporterAddress;
  openTrigger: React.ReactElement<HTMLButtonElement>;
}) => {
  const sectionAlerts = useLocalAlerts();
  const [isOpen, setIsOpen] = useState(false);

  const defaultFormValues: MailingAddressModal = {
    [MailingAddressEnum.Address]: addressDetails?.street ?? '',
    [MailingAddressEnum.Address2]: addressDetails?.street2 ?? '',
    [MailingAddressEnum.City]: addressDetails?.city ?? '',
    [MailingAddressEnum.Country]:
      mapCountryNameToCode(addressDetails?.country) ?? '',
    [MailingAddressEnum.State]: addressDetails?.state ?? '',
    [MailingAddressEnum.Postal]: addressDetails?.postalCode ?? '',
  };

  const { t } = useTranslation(['core', 'my-settings']);

  const handleSuccess = () => {
    sectionAlerts.addAlerts(createAddressUpdateSuccessAlert());
    setIsOpen(false);
    sectionAlerts.clearAlerts();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    sectionAlerts.clearAlerts();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent icon="mail-01" title={t('my-settings:form.mailing.title')}>
        <LocalAlertsProvider>
          <LocalAlerts />
          <MailingModalForm
            defaultFormValues={defaultFormValues}
            onSuccess={handleSuccess}
          />
        </LocalAlertsProvider>
      </DialogContent>
    </Dialog>
  );
};
